import { Delete, Person, PrecisionManufacturing } from "@mui/icons-material"
import { Box, IconButton, InputAdornment, TextField } from "@mui/material"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { Calculation, CalculationType } from "graphql/types"
import { memo, useCallback, useEffect, useState } from "react"
import {
  formatNumberForLocale,
  getNumberInputDisplayValue,
  normalizeNumberInput,
} from "utils/numberInputHelpers"

type AddCalculationType = {
  formState: Calculation
  setFormState: (arg: Calculation) => void
  disabled?: boolean
  deviceConnectionTotals?: Map<string, number>
  noDelete?: boolean
  disabledName?: boolean
}

export const AddCalculation = memo((props: AddCalculationType) => {
  const { formState, setFormState, disabled, deviceConnectionTotals, noDelete, disabledName } =
    props

  const calculationType = formState.calculationType ?? CalculationType.TIME

  const [humanPriceDisplay, setHumanPriceDisplay] = useState({
    price: getNumberInputDisplayValue(formState.humanPrice.price),
    cost: getNumberInputDisplayValue(formState.humanPrice.cost),
    amount: getNumberInputDisplayValue(formState.humanPrice.amount),
  })

  const [machinePriceDisplay, setMachinePriceDisplay] = useState({
    price: getNumberInputDisplayValue(formState.machinePrice.price),
    cost: getNumberInputDisplayValue(formState.machinePrice.cost),
    amount: getNumberInputDisplayValue(formState.machinePrice.amount),
  })

  const [connectionPriceDisplay, setConnectionPriceDisplay] = useState(
    formState.connectionPrice.map((price) => ({
      price: getNumberInputDisplayValue(price.price),
      cost: getNumberInputDisplayValue(price.cost),
      amount: getNumberInputDisplayValue(price.amount),
    }))
  )

  useEffect(() => {
    setHumanPriceDisplay({
      price: getNumberInputDisplayValue(formState.humanPrice.price),
      cost: getNumberInputDisplayValue(formState.humanPrice.cost),
      amount: getNumberInputDisplayValue(formState.humanPrice.amount),
    })
  }, [formState.humanPrice.amount, formState.humanPrice.cost, formState.humanPrice.price])

  useEffect(() => {
    setMachinePriceDisplay({
      price: getNumberInputDisplayValue(formState.machinePrice.price),
      cost: getNumberInputDisplayValue(formState.machinePrice.cost),
      amount: getNumberInputDisplayValue(formState.machinePrice.amount),
    })
  }, [formState.machinePrice.amount, formState.machinePrice.cost, formState.machinePrice.price])

  useEffect(() => {
    setConnectionPriceDisplay(
      formState.connectionPrice.map((price) => ({
        price: getNumberInputDisplayValue(price.price),
        cost: getNumberInputDisplayValue(price.cost),
        amount: getNumberInputDisplayValue(price.amount),
      }))
    )
  }, [formState.connectionPrice])

  const handleFormChangeOfferText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, offerText: e.target.value })
    },
    [formState, setFormState]
  )

  const handleHumanPriceChange = useCallback(
    (field: "price" | "cost" | "amount", value: string | number) => {
      const numericValue = typeof value === "string" ? normalizeNumberInput(value) : value
      setFormState({
        ...formState,
        humanPrice: {
          ...formState.humanPrice,
          [field]: numericValue,
        },
      })
    },
    [formState, setFormState]
  )

  const handleMachinePriceChange = useCallback(
    (field: "price" | "cost" | "amount", value: string | number) => {
      const numericValue = typeof value === "string" ? normalizeNumberInput(value) : value
      setFormState({
        ...formState,
        machinePrice: {
          ...formState.machinePrice,
          [field]: numericValue,
        },
      })
    },
    [formState, setFormState]
  )

  const handleConnectionPriceChange = useCallback(
    (index: number, field: "name" | "price" | "cost" | "amount", value: string | number) => {
      const updatedPrices = [...formState.connectionPrice]
      if (field === "name") {
        updatedPrices[index] = {
          ...updatedPrices[index],
          name: value as string,
        }
      } else {
        const numericValue = typeof value === "string" ? normalizeNumberInput(value) : value
        updatedPrices[index] = {
          ...updatedPrices[index],
          [field]: numericValue,
        }
      }
      setFormState({
        ...formState,
        connectionPrice: updatedPrices,
      })
    },
    [formState, setFormState]
  )

  const handleRemoveConnection = useCallback(
    (index: number) => {
      const updatedPrices = [...formState.connectionPrice]
      updatedPrices.splice(index, 1)
      setFormState({
        ...formState,
        connectionPrice: updatedPrices,
      })
    },
    [formState, setFormState]
  )

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
      }}
    >
      <TextField
        size="small"
        name="offerText"
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Angebotstext <HaufeIcon />
          </Box>
        }
        type="text"
        fullWidth
        value={formState.offerText}
        onChange={handleFormChangeOfferText}
        multiline
        rows={4}
      />

      <Box>
        {calculationType === CalculationType.TIME && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Person />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="price"
                label="Preis/h"
                type="text"
                value={humanPriceDisplay.price}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    price: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleHumanPriceChange("price", normalizedValue)
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    price: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    inputProps: { inputMode: "decimal" },
                  },
                }}
                disabled={disabled}
              />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="cost"
                label="Kosten/h"
                type="text"
                value={humanPriceDisplay.cost}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    cost: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleHumanPriceChange("cost", normalizedValue)
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    cost: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    inputProps: { inputMode: "decimal" },
                  },
                }}
                disabled={disabled}
              />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="amount"
                label="Dauer"
                type="text"
                value={humanPriceDisplay.amount}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    amount: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleHumanPriceChange("amount", normalizedValue)
                  setHumanPriceDisplay((prev) => ({
                    ...prev,
                    amount: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                fullWidth
                inputProps={{ inputMode: "decimal" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PrecisionManufacturing />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="price"
                label="Preis/h"
                type="text"
                fullWidth
                value={machinePriceDisplay.price}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    price: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleMachinePriceChange("price", normalizedValue)
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    price: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    inputProps: { inputMode: "decimal" },
                  },
                }}
                disabled={disabled}
              />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="cost"
                label="Kosten/h"
                type="text"
                fullWidth
                value={machinePriceDisplay.cost}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    cost: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleMachinePriceChange("cost", normalizedValue)
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    cost: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    inputProps: { inputMode: "decimal" },
                  },
                }}
                disabled={disabled}
              />
              <TextField
                sx={{
                  marginBottom: "10px",
                }}
                size="small"
                name="amount"
                label="Dauer"
                type="text"
                fullWidth
                value={machinePriceDisplay.amount}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    amount: value,
                  }))
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                  handleMachinePriceChange("amount", normalizedValue)
                  setMachinePriceDisplay((prev) => ({
                    ...prev,
                    amount: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                  }))
                }}
                inputProps={{ inputMode: "decimal" }}
              />
            </Box>
          </>
        )}

        {calculationType === CalculationType.CONNECTION && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {formState.connectionPrice.map((price, index) => (
              <Box
                key={`connection-${index}`}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  label="Anschluss"
                  type="text"
                  fullWidth
                  value={price.name}
                  onChange={(e) => {
                    handleConnectionPriceChange(index, "name", e.currentTarget.value)
                  }}
                  disabled={disabled || disabledName}
                />
                <TextField
                  size="small"
                  name="price"
                  label="Preis"
                  type="text"
                  fullWidth
                  value={connectionPriceDisplay[index]?.price ?? ""}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        price: value,
                      }
                      return next
                    })
                  }}
                  onBlur={(e) => {
                    const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                    handleConnectionPriceChange(index, "price", normalizedValue)
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        price: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                      }
                      return next
                    })
                  }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      inputProps: { inputMode: "decimal" },
                    },
                  }}
                  disabled={disabled}
                />
                <TextField
                  size="small"
                  name="cost"
                  label="Kosten"
                  type="text"
                  fullWidth
                  value={connectionPriceDisplay[index]?.cost ?? ""}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        cost: value,
                      }
                      return next
                    })
                  }}
                  onBlur={(e) => {
                    const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                    handleConnectionPriceChange(index, "cost", normalizedValue)
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        cost: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                      }
                      return next
                    })
                  }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      inputProps: { inputMode: "decimal" },
                    },
                  }}
                  disabled={disabled}
                />
                <TextField
                  size="small"
                  name="amount"
                  label="Menge"
                  type="text"
                  fullWidth
                  value={connectionPriceDisplay[index]?.amount ?? ""}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        amount: value,
                      }
                      return next
                    })
                  }}
                  onBlur={(e) => {
                    const normalizedValue = normalizeNumberInput(e.currentTarget.value)
                    handleConnectionPriceChange(index, "amount", normalizedValue)
                    setConnectionPriceDisplay((prev) => {
                      const next = [...prev]
                      next[index] = {
                        ...(next[index] || { price: "", cost: "", amount: "" }),
                        amount: normalizedValue === 0 ? "" : formatNumberForLocale(normalizedValue),
                      }
                      return next
                    })
                  }}
                  inputProps={{ inputMode: "decimal" }}
                />

                {deviceConnectionTotals && (
                  <TextField
                    size="small"
                    label="Alle EUTs"
                    fullWidth
                    value={deviceConnectionTotals.get(price.name) || 0}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled={true}
                  />
                )}

                {!disabled && !noDelete && (
                  <IconButton size="small" onClick={() => handleRemoveConnection(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            {/* {!disabled && (
              <StyledButton
                buttonType="custom"
                customText="Anschluss hinzufügen"
                customIcon={<Add />}
                onClick={handleAddConnection}
              />
            )} */}
          </Box>
        )}
      </Box>
    </Box>
  )
})
